'use client';

import ShinyText from '@/components/animations/ShinyText';
import StyledSplitText from '@/components/animations/StyledSplitText';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { HERO_CONTENT, HERO_STYLES } from '@/lib/constants/heroContent';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';

export default function Hero() {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.1, rootMargin: '50px' },
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (inView && videoRef.current) {
      const video = videoRef.current;
      const handleLoadedData = () => setVideoLoaded(true);

      video.addEventListener('loadeddata', handleLoadedData);
      return () => video.removeEventListener('loadeddata', handleLoadedData);
    }
  }, [inView]);

  return (
    <section className="flex flex-wrap flex-row items-center justify-center lg:gap-10 lg:px-16 lg:mx-auto px-8 xl:mt-4 mt-16">
      <div className="flex flex-col gap-6 md:max-w-[600px] max-w-[350px] flex-wrap lg:justify-center lg:items-start justify-start items-center w-full">
        <h1 className={`${HERO_STYLES.title} text-gray-900 max-w-4xl`}>
          <StyledSplitText
            words={HERO_CONTENT.title}
            className={HERO_STYLES.titleText}
            delay={150}
            duration={0.8}
            from={{ opacity: 0, y: 50 }}
            to={{ opacity: 1, y: 0 }}
            textAlign="left"
            as="span"
          />
        </h1>

        <p className="text-lg text-gray-600 leading-7">
          Genera imágenes hiperrealistas de ropa en modelos virtuales. Reduce costos, escala tu
          producción y personaliza al máximo tus campañas de e-commerce.
        </p>

        <div className="flex justify-start w-full">
          <Button
            variant="secondary"
            rightIcon={<ArrowRightIcon weight="bold" size={20} />}
            onClick={() => window.open('https://calendly.com/rial-ai/30min', '_blank')}
          >
            <ShinyText text="Agenda una demo" speed={7} className="text-white" />
          </Button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex justify-center items-center min-w-[300px] xl:w-[500px] lg:w-[400px] w-[600px] relative overflow-hidden"
      >
        <Loader className="xl:w-[500px] lg:w-[400px] w-[600px] h-[500px]" />

        {inView && (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster="/hero.webp"
            width={700}
            height={700}
            className={`absolute inset-0 object-contain w-full h-full transition-opacity duration-700 ${
              videoLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            aria-label="Decorative RIAL AI intro video"
          >
            <source src="/videos/rial-hero.webm" type="video/webm" />
            <source src="/videos/rial-hero.mp4" type="video/mp4" />
          </video>
        )}
      </div>
    </section>
  );
}
